import { createAsyncThunk } from '@reduxjs/toolkit';
import { Stomp } from '@stomp/stompjs';

import { resolveChatbotApiHost } from 'utils/api/api';
import { notify } from 'utils/toast/createToast';

import {
  GenerateDescriptionPayload,
  GeneratedDescriptionData,
  GeneratedJobResponse,
  JobGenerationRequest,
  SocketResponse
} from 'types/jobDescription';

import { JOB_DESCRIPTION_WEBSOCKET } from 'constants/endpoints';
import { AlertType } from 'constants/enums';
import { AppEnv } from 'constants/enums';

import { dropState } from './jobDescriptionSlice';

const APP_ENV = import.meta.env.VITE_ENV;
const resolveChatbotWsUrl = () =>
  `${resolveChatbotApiHost()}${JOB_DESCRIPTION_WEBSOCKET}`;

let stompClient: any = null;
let lastToken: string | null = null;
let lastJobId: string | null = null;

const generateUUID = () => crypto.randomUUID();

const showJobDescriptionError = (
  error: any,
  title: string,
  description: string,
  logPrefix = ''
) => {
  if (logPrefix) {
    console.error(logPrefix, error);
  }
  notify({
    type: AlertType.Error,
    message: title,
    description
  });
  return { success: false, error } as SocketResponse;
};

const reconnectIfNeeded = async (dispatch: any): Promise<void> => {
  if (!lastToken) {
    showJobDescriptionError(
      'No token',
      'Connection Error',
      'Authentication token not found'
    );
    return;
  }

  if (stompClient?.connected) {
    return;
  }

  console.log('[JobDescription] Reconnecting...');

  if (stompClient) {
    try {
      stompClient.disconnect();
    } catch (e) {
      console.warn('[JobDescription] Error disconnecting previous client:', e);
    }
  }

  const socketFactory = () => new WebSocket(resolveChatbotWsUrl());
  stompClient = Stomp.over(socketFactory);

  if (APP_ENV === AppEnv.Prod) {
    stompClient.debug = () => {};
  }

  try {
    await new Promise<void>((resolve, reject) => {
      stompClient.connect(
        { Authorization: lastToken, 'heart-beat': '10000,10000' },
        () => {
          console.log('[JobDescription] WebSocket reconnected');

          if (lastJobId) {
            stompClient.subscribe(
              `/job/${lastJobId}/queue/messages`,
              (msg: any) => {
                const message = JSON.parse(msg.body);
                if (message.type === 'GENERATED_JOB') {
                  dispatch(receiveGeneratedDescription(message));
                }
              }
            );
          }

          resolve();
        },
        (error: any) => {
          console.error('[JobDescription] Reconnection error:', error);
          stompClient = null;
          reject(error);
        }
      );
    });
  } catch (error) {
    showJobDescriptionError(
      error,
      'Reconnection Failed',
      'Unable to reconnect. Please try again.',
      '[JobDescription] Reconnection failed:'
    );
  }
};

export const initializeJobDescriptionSocket = createAsyncThunk(
  'jobDescription/initializeSocket',
  async (
    { jobId, token }: { jobId?: string; token: string | null },
    { dispatch }
  ) => {
    if (!token) {
      return;
    }
    lastToken = token;
    lastJobId = jobId || generateUUID();

    if (stompClient?.connected) {
      console.log('[JobDescription] Already connected');
      return { success: true, jobId: lastJobId } as SocketResponse;
    }

    if (stompClient) {
      try {
        stompClient.disconnect();
      } catch (e) {
        console.warn(
          '[JobDescription] Error disconnecting previous client:',
          e
        );
      }
    }

    try {
      const socketFactory = () => new WebSocket(resolveChatbotWsUrl());
      stompClient = Stomp.over(socketFactory);

      stompClient.heartbeatIncoming = 10000;
      stompClient.heartbeatOutgoing = 10000;

      if (APP_ENV === AppEnv.Prod) {
        stompClient.debug = () => {};
      }

      await new Promise<void>((resolve, reject) => {
        stompClient.connect(
          { Authorization: lastToken },
          () => {
            console.log('[JobDescription] WebSocket connected');

            stompClient.subscribe(
              `/job/${lastJobId}/queue/messages`,
              (msg: any) => {
                const message = JSON.parse(msg.body);
                console.log('[JobDescription] Received message:', message);
                if (message.type === 'GENERATED_JOB') {
                  dispatch(receiveGeneratedDescription(message));
                }
              }
            );

            resolve();
          },
          (error: any) => {
            console.error('[JobDescription] Connection error:', error);
            stompClient = null;
            reject(error);
          }
        );
      });

      return { success: true, jobId: lastJobId } as SocketResponse;
    } catch (error) {
      return showJobDescriptionError(
        error,
        'Connection Failed',
        'Unable to connect. Please try again.',
        '[JobDescription] Socket creation error:'
      );
    }
  }
);

export const receiveGeneratedDescription = createAsyncThunk(
  'jobDescription/receiveDescription',
  async (message: GeneratedJobResponse): Promise<GeneratedDescriptionData> => {
    return {
      description: message?.response?.description,
      qualifications: message?.response?.qualifications,
      skillRequirement: message?.response?.skillRequirement,
      language: message?.response?.languages
        ? Object.keys(message?.response?.languages)
        : null,
      workEnvironmentAndPhysicalRequirements:
        message?.response?.work_environment_and_physical_requirements
    };
  }
);

export const generateDescription = createAsyncThunk(
  'jobDescription/generate',
  async (
    { jobData, language, fieldsToGenerate }: GenerateDescriptionPayload,
    { dispatch }
  ) => {
    if (!stompClient?.connected) {
      console.log(
        '[JobDescription] Connection lost, attempting to reconnect...'
      );
      await reconnectIfNeeded(dispatch);
    }

    try {
      const message: JobGenerationRequest = {
        job: {
          ...jobData,
          id: lastJobId || generateUUID()
        },
        language,
        fields_to_generate: fieldsToGenerate
      };

      stompClient.send('/cw/generate', {}, JSON.stringify(message));
      return { success: true } as SocketResponse;
    } catch (error) {
      return showJobDescriptionError(
        error,
        'Generation Failed',
        'Failed to send generation request. Please try again.',
        '[JobDescription] Failed to generate:'
      );
    }
  }
);

export const disconnectJobDescription = createAsyncThunk(
  'jobDescription/disconnect',
  async (_, { dispatch }) => {
    if (!stompClient) {
      console.log('[JobDescription] No socket to disconnect');
      return { success: true } as SocketResponse;
    }

    try {
      if (stompClient.connected) {
        stompClient.disconnect(() => {
          console.log('[JobDescription] WebSocket disconnected');
        });
      }
    } catch (e) {
      console.warn('[JobDescription] Error while disconnecting:', e);
    }

    stompClient = null;
    lastToken = null;
    lastJobId = null;

    dispatch(dropState());

    return { success: true };
  }
);
